import { lazy, Suspense, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import RealtimeNotificationsBridge from './components/RealtimeNotificationsBridge';
import PullToRefresh from './components/PullToRefresh';
import ToastContainer from './components/ToastContainer';
import ConsentBanner from './components/ConsentBanner';
import { useAuth } from './contexts/AuthContext.jsx';
import { useShop } from './contexts/ShopContext.jsx';
import { useEffectiveRole, useIsEffectiveShopUser } from './hooks/useEffectiveRole';
import { getToken } from './utils/auth';
import { onBrowserFinished } from './utils/browser';
import { primeNotificationSound } from './utils/notificationSound';
import { getWebsiteUpgradeMessage, isBasicShop } from './utils/shopGuards';
import { showToast } from './utils/toast';

const Home = lazy(() => import('./views/Home.jsx'));
const Dashboard = lazy(() => import('./views/Dashboard.jsx'));
const AdminDashboard = lazy(() => import('./views/Admin/Dashboard.jsx'));
const AdminOrders = lazy(() => import('./views/Admin/Orders.jsx'));
const AdminOrderView = lazy(() => import('./views/Admin/OrderView.jsx'));
const AdminSearches = lazy(() => import('./views/Admin/Searches.jsx'));
const AdminReports = lazy(() => import('./views/Admin/Reports.jsx'));
const AdminRemuneration = lazy(() => import('./views/Admin/Remuneration.jsx'));
const AdminSectionPage = lazy(() => import('./views/Admin/SectionPage.jsx'));
const AdminAdvertisements = lazy(() => import('./views/Admin/Advertisements.jsx'));
const AdminBuyers = lazy(() => import('./views/Admin/Buyers.jsx'));
const AdminComplaints = lazy(() => import('./views/Admin/Complaints.jsx'));
const AdminPayments = lazy(() => import('./views/Admin/Payments.jsx'));
const AdminPayouts = lazy(() => import('./views/Admin/Payouts.jsx'));
const AdminPayoutView = lazy(() => import('./views/Admin/PayoutView.jsx'));
const AdminDispatcherPayoutView = lazy(() => import('./views/Admin/DispatcherPayoutView.jsx'));
const AdminShops = lazy(() => import('./views/Admin/Shops.jsx'));
const AdminShopView = lazy(() => import('./views/Admin/ShopView.jsx'));
const AdminDispatchers = lazy(() => import('./views/Admin/Dispatchers.jsx'));
const AdminDispatcherView = lazy(() => import('./views/Admin/DispatcherView.jsx'));
const AdminPromoWinners = lazy(() => import('./views/Admin/Promo.jsx'));
const AdminPromoEntries = lazy(() => import('./views/Admin/PromoEntries.jsx'));
const AdminSellers = lazy(() => import('./views/Admin/Sellers.jsx'));
const AdminSettings = lazy(() => import('./views/Admin/Settings.jsx'));
const AdminUsers = lazy(() => import('./views/Admin/Users.jsx'));
const AdminUserView = lazy(() => import('./views/Admin/UserView.jsx'));
const AdminWaybills = lazy(() => import('./views/Admin/Waybills.jsx'));
const BuyCredits = lazy(() => import('./views/BuyCredits.jsx'));
const Cart = lazy(() => import('./views/Cart.jsx'));
const BuyerProfile = lazy(() => import('./views/Buyer/Profile.jsx'));
const BuyerUserProfile = lazy(() => import('./views/Buyer/Users/Profile.jsx'));
const CategoryPage = lazy(() => import('./views/Categories.jsx'));
const Login = lazy(() => import('./views/Login.jsx'));
const OTPVerification = lazy(() => import('./views/OTPVerification.jsx'));
const PhoneEntry = lazy(() => import('./views/PhoneEntry.jsx'));
const PurchaseCallback = lazy(() => import('./views/PurchaseCallback.jsx'));
const Register = lazy(() => import('./views/Register.jsx'));
const RegisterBuyer = lazy(() => import('./views/Buyer/Register.jsx'));
const RegisterShop = lazy(() => import('./views/Shop/Register.jsx'));
const Section = lazy(() => import('./views/Section.jsx'));
const Sections = lazy(() => import('./views/Sections.jsx'));
const Segmentation = lazy(() => import('./views/Segmentation.jsx'));
const Shop = lazy(() => import('./views/Shop.jsx'));
const Shops = lazy(() => import('./views/Shops.jsx'));
const ShopView = lazy(() => import('./views/ShopView.jsx'));
const ShopAnalytics = lazy(() => import('./views/Shop/Analytics.jsx'));
const ShopAdvertising = lazy(() => import('./views/Shop/Advertising.jsx'));
const ShopAdvertisingFacebook = lazy(() => import('./views/Shop/AdvertisingFacebook.jsx'));
const ShopAdvertisingGoogle = lazy(() => import('./views/Shop/AdvertisingGoogle.jsx'));
const ShopDispatch = lazy(() => import('./views/Shop/Dispatch.jsx'));
const ShopLiveSessions = lazy(() => import('./views/Shop/LiveSessions.jsx'));
const ShopOrders = lazy(() => import('./views/Shop/Orders.jsx'));
const ShopBookkeeping = lazy(() => import('./views/Shop/Bookkeeping.jsx'));
const ShopPayments = lazy(() => import('./views/Shop/Payments.jsx'));
const ShopUserProfile = lazy(() => import('./views/Shop/Users/Profile.jsx'));
const ShopRequests = lazy(() => import('./views/Shop/Requests.jsx'));
const ShopWebsite = lazy(() => import('./views/Shop/Website.jsx'));
const StockEdit = lazy(() => import('./views/StockEdit.jsx'));
const Stocks = lazy(() => import('./views/Stocks.jsx'));
const BundleSale = lazy(() => import('./views/BundleSale.jsx'));
const StockView = lazy(() => import('./views/StockView.jsx'));
const Order = lazy(() => import('./views/Order.jsx'));
const UpgradePlan = lazy(() => import('./views/UpgradePlan.jsx'));
const Plans = lazy(() => import('./views/Plans.jsx'));
const RenewSubscription = lazy(() => import('./views/Subscription/Expired.jsx'));
const RenewSubscriptionPage = lazy(() => import('./views/Subscription/Renew.jsx'));
const AgentDashboard = lazy(() => import('./views/Agent/Dashboard.jsx'));
const CustomerServiceDashboard = lazy(() => import('./views/CustomerService/Dashboard.jsx'));
const DispatcherDashboard = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherDashboardPage })));
const DispatcherDeliveries = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherDeliveriesPage })));
const DispatcherInTransit = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherInTransitPage })));
const DispatcherQuotations = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherQuoteRequestsPage })));
const DispatcherCompany = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherCompanyPage })));
const DispatcherPayoutSetup = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherPayoutSetupPage })));
const DispatcherRates = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherRatesPage })));
const DispatcherRiders = lazy(() => import('./views/Dispatcher/Pages.jsx').then((mod) => ({ default: mod.DispatcherRidersPage })));
const ImageSearch = lazy(() => import('./views/ImageSearch.jsx'));
const BuyerDispatch = lazy(() => import('./views/Buyer/Dispatch.jsx'));
const BuyerOrders = lazy(() => import('./views/Buyer/Orders.jsx'));
const BuyerBroadcastRequest = lazy(() => import('./views/Buyer/BroadcastRequest.jsx'));
const BuyerRequestDetail = lazy(() => import('./views/Buyer/Request.jsx'));
const BuyerRequests = lazy(() => import('./views/Buyer/Requests.jsx'));
const BuyerHistory = lazy(() => import('./views/Buyer/History.jsx'));
const BuyerLikes = lazy(() => import('./views/Buyer/Likes.jsx'));
const BuyerPromo = lazy(() => import('./views/Buyer/Promo.jsx'));
const BuyerLiveSessions = lazy(() => import('./views/Buyer/LiveSessions.jsx'));
const MessageThread = lazy(() => import('./views/MessageThread.jsx'));
const Messages = lazy(() => import('./views/Messages.jsx'));
const LiveSessionView = lazy(() => import('./components/LiveSessionView.jsx'));

const AboutPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.AboutPage }))
);
const AppHubPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.AppHubPage }))
);
const BookkeepingPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.BookkeepingPage }))
);
const CategoriesPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.CategoriesPage }))
);
const DataDeletionPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.DataDeletionPage }))
);
const HelpCenterPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.HelpCenterPage }))
);
const ContactUsPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.ContactUsPage }))
);
const InventoryPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.InventoryPage }))
);
const OrderTrackingPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.OrderTrackingPage }))
);
const PrivacyPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.PrivacyPage }))
);
const RefundsPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.ReturnsPolicyPage }))
);
const DeliveryPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.ShippingPolicyPage }))
);
const DispatcherRegistrationPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.DispatcherRegistrationPage }))
);
const TermsPage = lazy(() =>
  import('./views/sitePages.jsx').then((module) => ({ default: module.TermsPage }))
);

function RouteFallback() {
  return (
    <div className="container py-5">
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading page...</span>
        </div>
      </div>
    </div>
  );
}

// Redirects shop_owner users away from buyer-facing pages
function BlockedForShopOwner({ children }) {
  const isShopUser = useIsEffectiveShopUser();
  return isShopUser ? <Navigate to="/dashboard" replace /> : children;
}

function BuyerRoleOnly({ children }) {
  const { currentUser } = useAuth();
  const effectiveRole = useEffectiveRole();

  if (currentUser && effectiveRole && effectiveRole !== 'buyer') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function CustomerServiceRoleOnly({ children }) {
  const effectiveRole = useEffectiveRole();
  const allowedRoles = ['csr', 'admin', 'super_admin', 'super_agent'];

  if (effectiveRole && !allowedRoles.includes(effectiveRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function HomeOrDashboard() {
  const { loading, currentUser } = useAuth();
  const effectiveRole = useEffectiveRole();

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (currentUser) {
    // Show Dashboard if effective role is a dashboard role, otherwise show Home
    const isDashboardRole =
      effectiveRole &&
      [
        'admin',
        'super_admin',
        'agent',
        'super_agent',
        'csr',
        'shop_owner',
        'employee',
        'dispatcher_owner',
        'dispatch_employee',
      ].includes(effectiveRole);
    return isDashboardRole ? <Dashboard /> : <Home />;
  }

  return <Home />;
}

function WebsiteAccessGuard({ children }) {
  const { shopDocument, shopFetched, shopLoading } = useShop();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/shop/website') return;
    if (shopLoading || !shopFetched) return;
    if (!isBasicShop(shopDocument)) return;

    showToast('warning', getWebsiteUpgradeMessage());
  }, [location.pathname, shopDocument, shopFetched, shopLoading]);

  if (
    location.pathname === '/shop/website' &&
    shopFetched &&
    !shopLoading &&
    isBasicShop(shopDocument)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { refreshShop } = useShop();
  const lastTrackedPathRef = useRef('');

  // Initialise push notifications once the user is authenticated
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    import('./services/pushNotifications')
      .then(({ initPushNotifications }) => {
        if (!cancelled) {
          return initPushNotifications(navigate);
        }
        return undefined;
      })
      .catch((error) => {
        console.error('Push notification init failed:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser, navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const unlockAudio = () => {
      primeNotificationSound();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio, { passive: true });
    window.addEventListener('touchstart', unlockAudio, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  useEffect(() => {
    const nextPath = `${location.pathname || '/'}${location.search || ''}`;
    if (lastTrackedPathRef.current === nextPath) return;
    if (typeof window !== 'undefined' && window.__ymLastTrackedPageView === nextPath) {
      lastTrackedPathRef.current = nextPath;
      return;
    }

    lastTrackedPathRef.current = nextPath;
    if (typeof window !== 'undefined') {
      window.__ymLastTrackedPageView = nextPath;
    }

    import('./services/marketingAnalytics')
      .then(({ trackPageView }) => {
        trackPageView({
          pathname: location.pathname,
          search: location.search,
          title: typeof document !== 'undefined' ? document.title : 'YubuMarket',
        });
      })
      .catch((error) => {
        console.error('Page tracking failed:', error);
      });
  }, [location.pathname, location.search]);

  // Bridge native Android back button to web history to improve navigation UX in Capacitor.
  useEffect(() => {
    let handle = null;
    let cancelled = false;
    import('@capacitor/app')
      .then(({ App: CapacitorApp }) =>
        Promise.resolve(
          CapacitorApp.addListener('backButton', () => {
            try {
              if (window.history && window.history.length > 1) {
                window.history.back();
              } else {
                CapacitorApp.exitApp();
              }
            } catch {
              // ignore
            }
          })
        )
      )
      .then((listenerHandle) => {
        if (cancelled && listenerHandle && typeof listenerHandle.remove === 'function') {
          listenerHandle.remove();
          return;
        }
        handle = listenerHandle;
      })
      .catch(() => {
        // Capacitor not available (web) or listener failed — ignore silently
      });

    return () => {
      cancelled = true;
      try {
        if (handle && typeof handle.remove === 'function') handle.remove();
      } catch {
        // ignore
      }
    };
  }, []);

  // When the native payment browser closes, refresh the shop context so guards and pages update.
  useEffect(() => {
    return onBrowserFinished(() => {
      try {
        if (typeof refreshShop === 'function') {
          refreshShop();
        }
      } catch {
        // ignore
      }
    });
  }, [refreshShop]);

  // Remove any Bootstrap-generated modal artifacts left on <body> after navigation
  useEffect(() => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  }, [location.pathname]);

  return (
    <>
      <ToastContainer />
      <ConsentBanner />
      <RealtimeNotificationsBridge token={currentUser ? getToken() : null} />
      <PullToRefresh />
      <Navbar />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomeOrDashboard />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/categories" element={<CategoryPage />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/help-center" element={<HelpCenterPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/delivery" element={<DeliveryPage />} />
          <Route path="/shipping-policy" element={<DeliveryPage />} />
          <Route path="/delivery/register-dispatcher" element={<DispatcherRegistrationPage />} />
          <Route path="/refunds" element={<RefundsPage />} />
          <Route path="/returns-policy" element={<RefundsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/apps" element={<AppHubPage />} />
          <Route path="/apps/inventory" element={<InventoryPage />} />
          <Route path="/apps/bookkeeping" element={<BookkeepingPage />} />
          <Route path="/apps/order-tracking" element={<OrderTrackingPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/buyer" element={<RegisterBuyer />} />
          <Route path="/register/shop" element={<RegisterShop />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/shop/users/profile" element={<ShopUserProfile />} />
          <Route
            path="/shops"
            element={
              <BlockedForShopOwner>
                <Shops />
              </BlockedForShopOwner>
            }
          />
          <Route
            path="/shops/:id/public"
            element={
              <BlockedForShopOwner>
                <ShopView />
              </BlockedForShopOwner>
            }
          />
          <Route path="/shops/:id" element={<Shop />} />
          <Route path="/shop/analytics" element={<ShopAnalytics />} />
          <Route path="/advertising" element={<ShopAdvertising />} />
          <Route path="/shop/dispatch" element={<ShopDispatch />} />
          <Route path="/shop/live-sessions" element={<ShopLiveSessions />} />
          <Route path="/shop/orders" element={<ShopOrders />} />
          <Route path="/shop/bookkeeping" element={<ShopBookkeeping />} />
          <Route path="/payments" element={<ShopPayments />} />
          <Route path="/shop/requests" element={<ShopRequests />} />
          <Route
            path="/shop/website"
            element={
              <WebsiteAccessGuard>
                <ShopWebsite />
              </WebsiteAccessGuard>
            }
          />
          <Route
            path="/profile"
            element={
              <BuyerRoleOnly>
                <BuyerProfile />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/profile"
            element={
              <BuyerRoleOnly>
                <BuyerProfile />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/users/profile"
            element={
              <BuyerRoleOnly>
                <BuyerUserProfile />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/Buyer/Users/Profile"
            element={
              <BuyerRoleOnly>
                <BuyerUserProfile />
              </BuyerRoleOnly>
            }
          />
          <Route path="/image-search" element={<ImageSearch />} />
          <Route
            path="/buyer/broadcast-request"
            element={
              <BuyerRoleOnly>
                <BuyerBroadcastRequest />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/Buyer/BroadcastRequest"
            element={
              <BuyerRoleOnly>
                <BuyerBroadcastRequest />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/requests"
            element={
              <BuyerRoleOnly>
                <BuyerRequests />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/requests/:id"
            element={
              <BuyerRoleOnly>
                <BuyerRequestDetail />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/history"
            element={
              <BuyerRoleOnly>
                <BuyerHistory />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/promo"
            element={
              <BuyerRoleOnly>
                <BuyerPromo />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/likes"
            element={
              <BuyerRoleOnly>
                <BuyerLikes />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/live-sessions"
            element={
              <BuyerRoleOnly>
                <BuyerLiveSessions />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/messages"
            element={
              <BuyerRoleOnly>
                <Messages />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/messages/:threadId"
            element={
              <BuyerRoleOnly>
                <MessageThread />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/dispatch"
            element={
              <BuyerRoleOnly>
                <BuyerDispatch />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/Buyer/Dispatch"
            element={
              <BuyerRoleOnly>
                <BuyerDispatch />
              </BuyerRoleOnly>
            }
          />
          <Route
            path="/buyer/orders"
            element={
              <BuyerRoleOnly>
                <BuyerOrders />
              </BuyerRoleOnly>
            }
          />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:threadId" element={<MessageThread />} />
          <Route path="/bundle_sales/:id" element={<BundleSale />} />
          <Route path="/stocks" element={<Stocks />} />
          <Route path="/sections" element={<Sections />} />
          <Route path="/sections/view" element={<Section />} />
          <Route path="/stocks/:id" element={<StockView />} />
          <Route path="/st/:slug" element={<StockView />} />
          <Route path="/order/:stockId" element={<Order />} />
          <Route path="/stocks/edit/:id" element={<StockEdit />} />
          <Route path="/live_sessions/:id" element={<LiveSessionView />} />
          <Route path="/video_sessions/:id" element={<LiveSessionView />} />
          <Route path="/upgrade_plan" element={<UpgradePlan />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/expired_subscription" element={<RenewSubscription />} />
          <Route path="/renew_subscription" element={<RenewSubscriptionPage />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/buy_credits" element={<BuyCredits />} />
          <Route path="/purchase_callback" element={<PurchaseCallback />} />
          <Route path="/advertising/google" element={<ShopAdvertisingGoogle />} />
          <Route path="/advertising/facebook" element={<ShopAdvertisingFacebook />} />
          <Route path="/advertising/facbook" element={<Navigate to="/advertising/facebook" replace />} />
          <Route path="/onboarding/phone" element={<PhoneEntry />} />
          <Route path="/onboarding/otp" element={<OTPVerification />} />
          <Route path="/onboarding/segment" element={<Segmentation />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/orders/:id" element={<AdminOrderView />} />
          <Route path="/admin/searches" element={<AdminSearches />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/remuneration" element={<Navigate to="/admin/remunerations" replace />} />
          <Route path="/admin/remunerations" element={<AdminRemuneration />} />
          <Route path="/orders/:id" element={<AdminOrderView />} />
          <Route path="/admin/dispatch" element={<AdminSectionPage title="Dispatch" icon="fas fa-truck" description="Dispatch tools will appear here." />} />
          <Route path="/admin/analytics" element={<AdminSectionPage title="Analytics" icon="fas fa-chart-line" description="Analytics tools will appear here." />} />
          <Route path="/admin/website" element={<AdminSectionPage title="Website" icon="fas fa-globe" description="Website tools will appear here." />} />
          <Route path="/admin/complaints" element={<AdminComplaints />} />
          <Route path="/admin/advertisements" element={<AdminAdvertisements />} />
          <Route path="/admin/waybillis" element={<AdminWaybills />} />
          <Route path="/admin/waybills" element={<AdminWaybills />} />
          <Route path="/admin/buyers" element={<AdminBuyers />} />
          <Route path="/admin/sellers" element={<AdminSellers />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/users/:id" element={<AdminUserView />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/payouts" element={<AdminPayouts />} />
          <Route path="/admin/payouts/:id" element={<AdminPayoutView />} />
          <Route path="/admin/payouts/dispatcher/:id" element={<AdminDispatcherPayoutView />} />
          <Route path="/admin/shops" element={<AdminShops />} />
          <Route path="/admin/shops/:id" element={<AdminShopView />} />
          <Route path="/admin/dispatchers" element={<AdminDispatchers />} />
          <Route path="/admin/dispatchers/:id" element={<AdminDispatcherView />} />
          <Route path="/admin/promo" element={<Navigate to="/admin/promo-winners" replace />} />
          <Route path="/admin/promo-winners" element={<AdminPromoWinners />} />
          <Route path="/admin/promo-entries" element={<AdminPromoEntries />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
          <Route path="/agent/:user_id" element={<AgentDashboard />} />
          <Route path="/csr" element={<CustomerServiceRoleOnly><CustomerServiceDashboard /></CustomerServiceRoleOnly>} />
          <Route path="/dispatcher/dashboard" element={<DispatcherDashboard />} />
          <Route path="/dispatcher/deliveries" element={<DispatcherDeliveries />} />
          <Route path="/dispatcher/in-transit" element={<DispatcherInTransit />} />
          <Route path="/dispatcher/quotations" element={<DispatcherQuotations />} />
          <Route path="/dispatcher/company" element={<DispatcherCompany />} />
          <Route path="/dispatcher/payout-setup" element={<DispatcherPayoutSetup />} />
          <Route path="/dispatcher/rates" element={<DispatcherRates />} />
          <Route path="/dispatcher/riders" element={<DispatcherRiders />} />
          <Route path="/sh/:slug" element={<ShopView />} />
        </Routes>
      </Suspense>
    </>
  );
}
